import { parse, serialize } from 'cookie'
import { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next'

export const AUTH_COOKIE_NAME = 'auth_token'
export const PREFERENCES_COOKIE_NAME = 'user_prefs'
export const MAX_AGE = 60 * 60 * 24 * 30 // 30 days

// Server-side cookie helpers
export function setAuthCookie(res: NextApiResponse, token: string) {
  const cookie = serialize(AUTH_COOKIE_NAME, token, {
    maxAge: MAX_AGE,
    expires: new Date(Date.now() + MAX_AGE * 1000),
    httpOnly: true,
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    sameSite: 'lax',
  })

  res.setHeader('Set-Cookie', cookie)
}

export function removeAuthCookie(res: NextApiResponse) {
  const cookie = serialize(AUTH_COOKIE_NAME, '', {
    maxAge: -1,
    path: '/',
  })

  res.setHeader('Set-Cookie', cookie)
}

// Server-side cookie getter
export function getAuthCookie(req: NextApiRequest | GetServerSidePropsContext['req']): string | null {
  // For API Routes
  if (req.cookies[AUTH_COOKIE_NAME]) {
    return req.cookies[AUTH_COOKIE_NAME]
  }

  // For pages - parse the cookie header
  const cookies = parse(req.headers.cookie || '')

  return cookies[AUTH_COOKIE_NAME] || null
}

// Client-side cookie helpers
export function setClientAuthCookie(token?: string) {
  if (!token) return
  
  const cookie = serialize(AUTH_COOKIE_NAME, token, {
    maxAge: MAX_AGE,
    expires: new Date(Date.now() + MAX_AGE * 1000),
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    sameSite: 'lax',
  })
  
  document.cookie = cookie
}

export function removeClientAuthCookie() {
  const cookie = serialize(AUTH_COOKIE_NAME, '', {
    maxAge: -1,
    path: '/',
  })
  
  document.cookie = cookie
}


export function setPreferencesCookie(
  res: NextApiResponse, 
  preferences: { showAdultContent: boolean },
) {
  const cookie = serialize(PREFERENCES_COOKIE_NAME, JSON.stringify(preferences), {
    maxAge: MAX_AGE,
    expires: new Date(Date.now() + MAX_AGE * 1000),
    // Not httpOnly since we might need to read this client-side
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    sameSite: 'lax',
  })

  res.setHeader('Set-Cookie', cookie)
}

export function getPreferencesCookie(
  req: NextApiRequest | GetServerSidePropsContext['req'],
): { showAdultContent: boolean } | null {
  // Default preferences
  const defaultPrefs = { showAdultContent: false }
  
  // For API Routes
  if (req.cookies[PREFERENCES_COOKIE_NAME]) {
    try {
      return JSON.parse(req.cookies[PREFERENCES_COOKIE_NAME])
    } catch (e) {
      return defaultPrefs
    }
  }

  // For pages
  const cookies = parse(req.headers.cookie || '')
  
  if (cookies[PREFERENCES_COOKIE_NAME]) {
    try {
      return JSON.parse(cookies[PREFERENCES_COOKIE_NAME])
    } catch (e) {
      return defaultPrefs
    }
  }
  
  return defaultPrefs
}

export function setClientPreferencesCookie(preferences: { showAdultContent: boolean }) {
  const cookie = serialize(PREFERENCES_COOKIE_NAME, JSON.stringify(preferences), {
    maxAge: MAX_AGE,
    expires: new Date(Date.now() + MAX_AGE * 1000),
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    sameSite: 'lax',
  })
  
  document.cookie = cookie
}
